/**
 * @file 统一操作 API
 *
 * @example
 *
 *  - import { $api } from '@/utils/api';
 *  - $api.getDemo
 */
import axios from '@/utils/axios';
import { formatParams } from '@/utils/tools';
import { defaultLang, defaultLegalFlag } from '@/config/setup';
import { getDeviceTokenReqId } from '@/utils/deviceToken';

export const $api = {
  // 获取所有币种
  getCurrency() {
    return axios.get('@api/basic/tokens');
  },

  // 所有币对列表
  getSymbols() {
    return axios.get('@api/basic/symbols');
  },
  // 获取币对分类
  getSymbolCategory() {
    return axios.get('@capi/v3/basic/symbol/category');
  },
  // 币，币对，币选项
  getConfig() {
    // return axios.get('https://bapi.toobit.com/api/basic/config_v2?type=all');
    return axios.get('@api/basic/config_v2?type=all');
  },

  // 首页信息
  getIndex_config(lang?: any) {
    return axios.get('@api/basic/index_config?', {
      headers: { 'Accept-Language': lang }
    });
  },
  // 币种详情
  token_info(params: object) {
    return axios.get('@bapi/v1/basic/token', { params });
  },
  // 获取热门交易对
  getHotSymbol(params: any) {
    return axios.get('@bapi/v1/basic/hot_new_symbols', { params });
  },

  // 支持的语言
  getSupportLanguages(params: any) {
    return axios.get('@bapi/v1/basic/support_languages', { params });
  },

  // 支持的法币
  getSupportLegalCurrency(params: any) {
    return axios.get('@bapi/v1/basic/support_currency', { params });
  },

  // 是否隐藏页面入口（信用卡买币）
  getHideConfig(params: any) {
    return axios.get('@bapi/v1/basic/hide-config', { params });
  },
  // RTC IP 限制
  getRiskIP(params: any) {
    return axios.get('@bapi/v1/basic/ip-risk', { params });
  },
  networkData(data: any) {
    return axios.post('@uapi/v1/device-token/net-data/report', data, {
      crypto: true,
      headers: { 'Content-Type': 'application/json' }
    });
  },
  // 获取腾讯设备id
  getTdidDeviceId(params: { requestId: string; isSync?: boolean }) {
    return axios.get('@uapi/v1/device-token/get', { params });
  },

  // 腾讯设备指纹上报
  tdidDeviceTokenReport(data: any) {
    return axios.post(
      '@uapi/v1/device-token/report-v3',
      {
        requestId: data.requestId,
        deviceToken: data.visitorId
      },
      { headers: { 'Content-Type': 'application/json' } }
    );
  },

  // 设备指纹上报
  deviceTokenReport(data: any) {
    return axios.post(
      '@uapi/v1/device-token/report',
      {
        requestId: data.requestId,
        deviceToken: data.visitorId
      },
      { headers: { 'Content-Type': 'application/json' } }
    );
  },
  // 极验证验证方式
  getVRT(params: any) {
    return axios.get('@capi/v1/basic/vrt', { params });
  },
  // USDC 0 Free
  getUsdcFree() {
    return axios.get('@bapi/v1/basic/symbol/zero-fee-usdc');
  },
  // 获取用户当前IP
  getUserIp() {
    return axios.get('@bapi/v1/basic/current-ip');
  }
};

// 登录注册找回密码个人中心
export const ucApi = {
  // 邮箱注册
  emailRegister(data: object, params?: any) {
    return axios.post(`@capi/user/email_sign_up?d=${params?.d}&dReqId=${getDeviceTokenReqId()}`, data, {
      crypto: true
    });
  },
  // 手机注册
  mobileRegister(data: object, params?: any) {
    return axios.post(`@capi/user/mobile_sign_up?d=${params?.d}&dReqId=${getDeviceTokenReqId()}`, data, {
      crypto: true
    });
  },
  // 登出
  logout(data: object) {
    return axios.post('@capi/user/authorize_cancel', data);
  },
  // 未登录状态 邮箱验证码
  sendEmailVerifyCode(data: object) {
    return axios.post('@capi/common/send_email_verify_code', data);
  },
  // 未登录状态 手机验证码
  sendSmsVerifyCode(data: object) {
    return axios.post('@capi/common/send_sms_verify_code', data, {
      crypto: true
    });
  },
  // 未登录状态验证码 不需要极验
  sendCommonVerifyCode(data: object) {
    return axios.post('@api/common/send_verify_code', data);
  },
  // 国家区号前缀
  getCountries(data: object) {
    return axios.post('@capi/basic/countries', data);
  },
  userinfo() {
    return axios.get('@capi/v1/user/get_base_info');
  },
  // 登录态
  isLogin() {
    return axios.get('@capi/v1/user/simple-base-info');
  },

  // 手机或邮箱登录
  login(data: object) {
    return axios.post(`@capi/user/authorize?dReqId=${getDeviceTokenReqId()}`, data, { crypto: true });
  },

  // 登录时，二次验证邮箱
  send_email_verify_code_authorize_advance(data: object) {
    return axios.post(
      '@capi/user/send_email_verify_code/authorize_advance',
      data
    );
  },
  // 登录时，二次验证手机验证码
  send_sms_verify_code_authorize_advance(data: object) {
    return axios.post(
      '@capi/user/send_sms_verify_code/authorize_advance',
      data
    );
  },
  // 登录二次验证
  login_step2(data: object) {
    return axios.post(`@capi/user/authorize_advance?dReqId=${getDeviceTokenReqId()}`, data, { crypto: true });
  },
  // 手机重置密码-检查接口
  resetPasswordCheckMobile(data: object) {
    return axios.post('@api/user/mobile_find_pwd_check1', data);
  },
  // 邮箱重置密码-检查接口
  resetPasswordCheckEmail(data: object) {
    return axios.post('@api/user/email_find_pwd_check1', data);
  },
  // 重置密码-安全验证码发送
  resetPwdSendCode(data: object) {
    return axios.post('@api/user/send_verify_code/find_pwd', data);
  },
  // 重置密码-安全验证码
  resetPwdVerify2FA(data: object) {
    return axios.post('@api/user/find_pwd_check2', data);
  },
  // 重置密码
  resetPassword(data: object) {
    return axios.post('@api/user/find_pwd', data);
  },

  // 校验手机号
  registerPreCheck(data: object) {
    return axios.post('@capi/v1/user/register/pre-check', data, {
      headers: { 'Content-Type': 'application/json' }
    });
  },
  // 获取三方预处理信息
  thirdParty(params: any) {
    return axios.get('@capi/v1/user/third_party/pre', { params });
  },
  // 处理三方结果接口
  authorize(data: object) {
    return axios.post(`@capi/v1/user/third_party/authorize?dReqId=${getDeviceTokenReqId()}`, data);
  },
  // 绑定三方登录
  thirdPartyBind(data: object) {
    return axios.post('@bapi/v1/user/third_party/bind', data);
  },
  // 解绑三方登录
  thirdPartyUnbind(data: object) {
    return axios.post('@bapi/v1/user/third_party/unbind', data);
  },
  // 处理三方验证结果
  authorizeAdvance(data: object) {
    return axios.post(`@capi/v1/user/third_party/authorize_advance?dReqId=${getDeviceTokenReqId()}`, data);
  },

  // telegram id_token
  getTelegramToken() {
    return axios.get('@uapi/v1/tg/app/user/getWebAppData');
  },

  // 获取登录信息
  getLoginInfo() {
    return axios.get('@capi/user/login-info');
  },

  // ----------------------------------V2接口----------------------------------

  // 手机重置密码-检查接口
  resetPasswordCheckMobileV2(data: object) {
    return axios.post('@bapi/v1/user/v2/mobile-find-pwd-check1', data);
  },
  // 邮箱重置密码-检查接口
  resetPasswordCheckEmailV2(data: object) {
    return axios.post('@bapi/v1/user/v2/email_find_pwd_check1', data);
  },
  resetPwdVerify2FAV2(data: object) {
    return axios.post('@bapi/v1/user/v2/find-pwd', data);
  },
  // 解绑三方登录
  thirdPartyUnbindV2(data: object) {
    return axios.post('@bapi/v1/user/v2/third_party/unbind', data);
  },
  getIsAllowBind() {
    return axios.get('@bapi/v1/invitation/is-allow-bind');
  },
  // 绑定邀请码
  bindInviteCode(data?: object) {
    return axios.post('@bapi/v1/invitation/bind-invite-code', data, {
      headers: { 'Content-Type': 'application/json' }
    });
  },
  // 修改称昵
  updateNickName(data?: any) {
    return axios.post(
      `@bapi/v1/user/update_nickname?nickname=${data.nickname}`,
      {},
      {
        headers: { 'Content-Type': 'application/json' }
      }
    );
  },
  // 修改头像
  updateProfilePicture(data?: any) {
    return axios.post(
      `@bapi/v1/user/update_avatar?avatar=${data.avatar}`,
      {},
      {
        headers: { 'Content-Type': 'application/json' }
      }
    );
  },
  // 系统头像列表
  userSystemAvatar() {
    return axios.get('@bapi/v1/user/system_avatar');
  },

  userUpdateUserInfo(data?: any) {
    return axios.post('@bapi/v1/user/update_user_info', data, {
      headers: { 'Content-Type': 'application/json' }
    });
  },

  // 国家信息
  getBasicCountries(params: any) {
    return axios.get('@bapi/v1/basic/countries', { params });
  },
  getFrozenList(params: any) {
    return axios.get('@capi/v1/user/frozen/info', { params });
  }
};

export const baseAPI = {
  // 通用配置
  getCommonConfig() {
    return axios.get('@capi/v2/basic/common_config');
  },
  // 币种列表
  getTokenList(params?: object) {
    return axios.get('@capi/v3/basic/token/list', { params });
  },
  // 合约交易对
  getFuturesSymbol(params?: object) {
    return axios.get('@capi/v3/basic/symbol/futures', { params });
  },
  // 获取现货交易对 是否允许绑定
  getSpotSymbol(params?: object) {
    return axios.get('@capi/v3/basic/symbol/spot', { params });
  },
  // 首页 获取24小时成交量数据
  getIndexAmount() {
    return axios.get('@uapi/v1/index/data/amount');
  },
  // 首页 获取注册人数
  getIndexUserCount() {
    return axios.get('@uapi/v1/index/data/userCount');
  },
  // 首页 获取币对数量
  getIndexSymbolCount() {
    return axios.get('@uapi/v1/index/data/symbolCount');
  },
  GetApiPublicKey() {
    return axios.get('@capi/basic/api-public-key');
  },
  GetFuturesStopInfo() {
    return axios.get('@bapi/v1/futures/stop/info');
  }
};

// 用户登录后 查询获取 验证码
export const userApi = {
  // 币余额查询
  getUserLoginList(data: object) {
    // https://www.wcsbapp.com@api/?c_token=U6ZyvgcjYVGBWpFjXGdMlC6uquYP16H82
    return axios.post('@api/user/authorize_log', data);
  },
  // 获取用户登录记录
  getUserLoginDevice(params: object) {
    return axios.get('@bapi/v1/user/login-device', { params });
  },
  logoutDevice(data: object) {
    return axios.post('@bapi/v1/user/login-device/log-out', data, {
      headers: { 'Content-Type': 'application/json' }
    });
  },
  // 登录后获取邮箱验证码
  sendEmailVerifyCode(data: object) {
    return axios.post('@capi/user/send_email_verify_code', data);
  },
  // 登录后获取手机验证码
  sendSmsVerifyCode(data: object) {
    return axios.post('@capi/user/send_sms_verify_code', data);
  },
  // 邮箱登录 绑定手机号
  bindMobile(data: object) {
    return axios.post('@api/user/bind_mobile', data);
  },
  // 手机登录 绑定邮箱
  bindEmail(data: object) {
    return axios.post('@api/user/bind_email', data);
  },
  // 获取GA secretKey  before_bind_ga
  beforeBindGa(data: object) {
    return axios.post('@api/user/before_bind_ga', data);
  },
  // 换绑获取GA secretKey  before_bind_ga
  beforeChangeBindGa(data: object) {
    return axios.post('@api/user/before_alter_ga', data);
  },
  // recaptcha.net
  // 114.250.64.34
  // 绑定GA
  bindGa(data: object) {
    return axios.post('@api/user/bind_ga', data);
  },

  // 获取用户KYC级别信息
  getUserKycInfo() {
    return axios.get('@api/user/kyclevel');
  },
  // 实名信息
  getVerifyInfo() {
    return axios.get('@api/user/verify_info');
  },
  // 换绑GA
  changeBindGa(data: object) {
    return axios.post('@api/user/alter_ga', data);
  },
  // 换绑email
  changeBindEmail(data: object) {
    return axios.post('@api/user/alter_email', data);
  },
  // 换绑mobile
  changeBindMobile(data: object) {
    return axios.post('@api/user/alter_mobile', data);
  },
  // 设置防钓鱼码 edit_anti_phishing_code
  fishCode(data: object) {
    return axios.post('@api/user/edit_anti_phishing_code', data);
  },
  // 设置资金密码
  fundPassword(data: object) {
    return axios.post('@api/user/trade_pwd/set', data);
  },
  // 修改登录密码
  updatePassword(data: object) {
    return axios.post('@api/user/update_pwd', data);
  },
  // 获取证件类别
  getIdCardType() {
    return axios.get('@api/basic/id_card_type');
  },
  // 解绑GA
  unbindGa(data: object) {
    return axios.post('@api/user/unbind_ga', data);
  },
  // 获取ga解绑状态
  gaApply(data: object) {
    return axios.get('@bapi/v1/user/v2/unbind-ga/apply/info', data);
  },
  // 提交ga解绑申请
  unbindGaApply(data: object) {
    return axios.post('@bapi/v1/user/v2/unbind-ga/apply', data);
  },
  // 生成2fa id
  verify2fa(par: string) {
    return axios.post(
      `@bapi/v1/user/v2/verify-2fa?${par}`,
      {},
      {
        headers: { 'Content-Type': 'application/json' }
      }
    );
  },
  // unbind_email 解绑邮箱
  unbindEmail(data: object) {
    return axios.post('@api/user/unbind_email', data);
  },
  // unbind_mobile 解绑手机号
  unbindMobile(data: object) {
    return axios.post('@api/user/unbind_mobile', data);
  },
  // 用户kyc基础认证
  kycBasicVerify(data: object) {
    return axios.post('@api/user/kyc/basicverify', data);
  },

  // 用户证件照KYC认证
  kycphotoverify(data: object) {
    return axios.post('@api/user/kyc/photoverify', data);
  },

  // 获取子账户列表 account/sub_account/query
  queryAccountList(data: object) {
    return axios.post('@api/account/sub_account/query', data);
  },
  // 获取api列表  api_keys
  getApiList(data: object) {
    return axios.post('@api/user/api_keys', data);
  },
  // 创建api  user/api_key/create
  createApiKey(data: object) {
    return axios.post('@api/user/api_key/create', data);
  },
  // 更新api  user/api_key/update_ips
  updateApiKeys(data: object) {
    return axios.post('@api/user/api_key/update_ips', data);
  },
  // 删除api key    api_key/delete
  delApiKeys(data: object) {
    return axios.post('@api/user/api_key/delete', data);
  },
  // 修改api key 状态 user/api_key/change_status
  changeApiKeyStatus(data: object) {
    return axios.post('@api/user/api_key/change_status', data);
  },
  // 获取邀请码 invitation/info/get
  getInviteCode(data: object) {
    return axios.post('@api/invitation/share/template_info', data);
  },
  // 获取邀请数据 invitation/info/get
  getInviteData(data: object) {
    return axios.post('@api/invitation/info/get', data);
  },
  // 获取邀请列表 invitation/relation/list?
  getInviteList(data: object) {
    return axios.post('@api/invitation/relation/list', data);
  },
  // 返佣记录明细列表 invitation/bonus/list
  getReturnBonusList(data: object) {
    return axios.post('@api/invitation/bonus/list', data);
  },
  // 获取消息列表
  getMessageList(par: object) {
    return axios.get('@uapi/v1/notification/message/page-list', {
      params: par
    });
  },
  // 获取未读消息数量
  getUnreadMessage(par: object) {
    return axios.get('@uapi/v1/notification/message/unread-cnt', {
      params: par
    });
  },
  // 查询消息类型
  getMessageModuleList(par: object) {
    return axios.get('@uapi/v1/notification/module/list', { params: par });
  },
  // 清除全部消息
  clearMessageList(par: object) {
    return axios.post('@uapi/v1/notification/message/clear-all', par, {
      headers: { 'Content-Type': 'application/json' }
    });
  },
  // 阅读消息
  readMessage(par: object) {
    return axios.post('@uapi/v1/notification/module/read', par, {
      headers: { 'Content-Type': 'application/json' }
    });
  },
  // 全部已读
  readAllMessage(par: object) {
    return axios.post('@uapi/v1/notification/module/read-all', par, {
      headers: { 'Content-Type': 'application/json' }
    });
  },

  // ----------------------------------用户的V2接口----------------------------------
  getVerifyType(par: object) {
    // /bapi/v1/user/v2/auth-types
    return axios.get('@capi/v1/user/v2/auth-types', { params: par });
  },
  // 绑定手机号
  bindMobileV2(data: object) {
    return axios.post('@bapi/v1/user/v2/bind-mobile', data);
  },
  // 绑定邮箱
  bindEmailV2(data: object) {
    return axios.post('@bapi/v1/user/v2/bind-email', data);
  },
  // 绑定GA
  bindGaV2(data: object) {
    return axios.post('@bapi/v1/user/v2/bind-ga', data);
  },
  // 解绑GA
  unbindGaV2(data: object) {
    return axios.post('@bapi/v1/user/v2/unbind-ga', data);
  },
  // unbind_email 解绑邮箱
  unbindEmailV2(data: object) {
    return axios.post('@bapi/v1/user/v2/unbind-email', data);
  },
  // unbind_mobile 解绑手机号
  unbindMobileV2(data: object) {
    return axios.post('@bapi/v1/user/v2/unbind-mobile', data);
  },
  // 换绑邮箱
  changeBindEmailV2(data: object) {
    return axios.post('@bapi/v1/user/v2/alter-email', data);
  },
  // 换绑手机
  changeBindMobileV2(data: object) {
    return axios.post('@bapi/v1/user/v2/alter-mobile', data);
  },
  // 换绑GA
  changeBindGaV2(data: object) {
    return axios.post('@bapi/v1/user/v2/alter-ga', data);
  },
  // 修改登录密码
  updatePasswordV2(data: object) {
    return axios.post('@bapi/v1/user/v2/update-pwd', data);
  },
  // 获取登录用户的链白名单
  whiteList() {
    return axios.get('@bapi/v1/basic/private');
  },
  // 获取登录用户的链白名单
  getGenerate(data: any) {
    return axios.post('@capi/v1/user/qr-code/generate', data);
  },
  qrCodeInfo(params: any) {
    return axios.get('@capi/v1/user/qr-code/info', { params });
  },
  qrCodeLogin(data: any) {
    return axios.post('@capi/v1/user/qr-code/login', data, {
      headers: { 'Content-Type': 'application/json' }
    });
  },
  // 进入身份认证页面直接调用，返回初级与高级的认证系统
  getKycConfig() {
    return axios.get('@bapi/v1/user/kyc/config');
  },
  // 是否可以kyc，不可以kyc，KYC初级认证超过次数返回code：31537，高级认证超过次数返回CODE：31538
  getKycLevelToken(params: any) {
    return axios.get('@bapi/v1/user/kyc/kycLevelToken', { params });
  },
  // 解除KYC申请
  kycLiftApply(data: any) {
    return axios.post('@bapi/v1/user/kyc/lift/apply', data, {
      headers: { 'Content-Type': 'application/json' }
    });
  },
  // 通知后端完成了KYC
  kycPush(data: any) {
    return axios.post('@bapi/v1/user/kyc/push', data, {
      headers: { 'Content-Type': 'application/json' }
    });
  },
  // 上传视频
  uploadVideo(data: any) {
    return axios.post('@bapi/v1/user/upload-video/v2', data);
  },
  // check 波斯用户的证件号验证是否存在
  checkPersianIdNo(params: { card_no: string }) {
    return axios.get('@bapi/v1/kyc/cardno-check', { params });
  },
  // 提交波斯kyc
  submitPersianKyc(data: any) {
    return axios.post('@bapi/v1/kyc/persian-verify', data);
  }
};

// /bapi/v1/copy-trading/copy-trading-asset-info

export const assetApi = {
  // 币余额查询
  getAsset(params: any = '') {
    return axios.get('@capi/asset/get', { params });
  },
  // 跟单资产与带单资产
  getCopyTradingAssetInfo(params: object) {
    return axios.get('@bapi/v1/copy-trading/copy-trading-asset-info', {
      params
    });
  },

  // 跟单资产 资产流水
  getCopyBalanceFlow(params: object) {
    return axios.get('@api/v1/copy-trading/asset/balance_flow', { params });
  },
  // 理财资产
  getSummaryFund(params: object) {
    return axios.get('@bapi/v1/earn/summaryFund', { params });
  },
  // 理财资产
  getEarnFund(params: object) {
    return axios.get('@bapi/v1/earn/funds', { params });
  },
  // 总资产
  getTotalAsset(params: object) {
    return axios.get('@capi/v1/asset/info', { params });
  },

  // 获取汇率
  getRates() {
    return axios.post('@api/quote/v1/ratesV2', {
      legalCoins: [defaultLegalFlag(), 'USD'].join(',')
    });
  },

  // 下单
  createOrder(data: object) {
    return axios.post('@capi/order/create', data);
  },

  // 撤单
  cancelOrder(data: object) {
    return axios.post('@capi/order/cancel', data);
  },

  // 全部撤单
  cancelAllOrder(data: object = {}) {
    return axios.post('@capi/order/batch_cancel', data);
  },

  // 计划委托下单
  createPlanOrder(data: object) {
    return axios.post('@capi/order/plan_spot/create', data);
  },

  // 取消计划委托
  cancelPlanOrder(data: object) {
    return axios.post('@capi/order/plan_spot/cancel', data);
  },

  // 批量取消计划委托
  cancelAllPlanOrder(data: object) {
    return axios.post('@capi/order/plan_spot/batch_cancel', data);
  },

  // 当前委托
  openOrders(params: object) {
    const param = JSON.parse(JSON.stringify(params));
    delete param.source;
    return axios.post('@capi/order/open_orders', param);
  },

  // 历史委托，拉取历史数据
  historyOrders(params: object) {
    const param = JSON.parse(JSON.stringify(params));
    delete param.source;
    return axios.post('@api/order/trade_orders', param);
  },

  // 历史委托，拉取历史数据
  historyRrades(params: object) {
    const param = JSON.parse(JSON.stringify(params));
    delete param.source;
    return axios.post('@api/order/my_trades', param);
  },

  // 成交明细
  matchInfo(params: object) {
    return axios.post('@api/order/match_info', params);
  },
  // get_ws_token: `${prefix}-/user/get_ws_token`, // 获取websocket握手token ,// 订单ws连接前，握手接口

  // 卖出配置
  sellConfig(params: object) {
    return axios.post('@capi/order/sell_buy/config', params);
  },

  // 获取当前计划委托
  openPlanOrders(params: object) {
    const param = JSON.parse(JSON.stringify(params));
    delete param.source;
    return axios.post('@capi/order/plan_spot/open_orders', param);
  },

  // 获取历史计划委托
  historyPlanOrders(params: object) {
    const param = JSON.parse(JSON.stringify(params));
    delete param.source;
    return axios.post('@api/order/plan_spot/trade_orders', param);
  },

  // 获取计划委托明细
  getPlanOrderDetail(params: object) {
    return axios.post('@api/order/plan_spot/get', params);
  },

  // 资产划转
  assetTransfer(params: object) {
    return axios.post('@capi/v1/account/transfer', params);
  },

  // 账户资产
  getAccount() {
    return axios.get('@capi/account/get');
  },

  // 可划转资产余额查询
  transferAvailable(params: object) {
    // 新换的接口
    return axios.get('@capi/v1/transfer/available', { params });
    // return axios.get('@bapi/v1/account/sub-account/transfer-available', { params });
  },

  // 创建子账户
  createAccount(params: object) {
    return axios.post('@api/account/sub_account/create', params);
  },

  // 子账户类型
  accountType(params: object) {
    return axios.get('@api/basic/sub_account/support_account_type_v1', params);
  },

  // 账户列表
  accountList() {
    return axios.get('@api/account/sub_account/query');
  },

  // 禁止划转设置
  // accountLimit(params: object) {
  //   return axios.get('@api/v1/account/sub_account/transfer/limit', params);
  // },

  // 获取近期充提记录
  // /account/deposit-withdraw-list
  depositWithdrawList(params: object) {
    return axios.get('@api/account/deposit-withdraw-list', { params });
  },

  // 请求所有子账户
  sendSubAccount() {
    return axios.get('@capi/v1/account/sub-accounts-info');
  },
  // 查询可划转金额
  getTransferAvailable(params: object) {
    return axios.get('@bapi/v1/account/transfer-available', { params });
  },
  // 账户划转
  sendAccountTransfer(data: object) {
    return axios.post('@capi/v1/account/transfer', data);
  },
  // 是否显示合约盈亏
  // /bapi/user_futures/switch
  getFuturesSwitch(params: object) {
    return axios.get('@bapi/user_futures/switch', { params });
  },
  // 获取用户当天的盈亏
  getTodayPnl(params: object) {
    return axios.get('@bapi/user_futures/today_pnl', { params });
  },
  // 获取用户累计盈亏
  getTotalPnl(params: object) {
    return axios.get('@bapi/user_futures/total_pnl', { params });
  },
  // 获取用户盈亏日历
  getCalendarView(params: object) {
    return axios.get('@bapi/user_futures/calendar_view', { params });
  },
  getFrozenOrder(params: object) {
    return axios.get('@capi/v1/deposit/frozenOrders', { params });
  },
  // 获取用户交易数量
  getTotalTrade(params: object) {
    return axios.get('@bapi/user_futures/trade_volume_view', { params });
  },
  // 获取合约策略账户信息
  getStrategyAccount(params: object) {
    return axios.get('@bapi/v1/futures-strategy/strategy-account', { params });
  },

  // 创建oco订单
  createOcoOrder(data: object) {
    return axios.post('@bapi/v1/spot/order', data, {
      headers: { 'Content-Type': 'application/json' }
    });
  },
  // 获取储备金
  getReserveAssets() {
    return axios.get('@bapi/v1/basic/exchange_assets');
  },
  // 下载历史记录
  downloadHistory(data: object) {
    return axios.post('@bapi/v1/download/apply', data, {
      headers: { 'Content-Type': 'application/json' }
    });
  },
  // 获取下载记录
  getDownloadHistory(params: object) {
    return axios.get('@bapi/v1/download/apply-list', { params });
  },
  // 下载导出记录
  downloadExportRecordFileUrl(params: object) {
    return axios.get('@bapi/v1/download/get-link', { params });
  }
};

// 现货接口
export const spotApi = {
  // 快速上币活动满足资格
  quickSymbolQualifying(params: object) {
    return axios.get('@bapi/v1/open_activity/participation_info', { params });
  },
  // 币对配置
  symbolConfig() {
    return axios.get('@bapi/v1/basic/symbol_config');
  },
  // 添加自选
  favoriteAdd(data: object) {
    return axios.post('@api/user/favorite/create', data);
  },
  // 取消自选
  favoriteCancel(data: object) {
    return axios.post('@api/user/favorite/cancel', data);
  },
  // 获取币种列表哦
  getSpotSymbolList() {
    return axios.get('@api/contract/funding_rates', {});
  },
  // 收益率计算
  getProfitInfo(params: object) {
    return axios.get('@bapi/v1/futures/calculator/profit_info', { params });
  },
  // 预估强平价
  getLiquidationPrice(params: object) {
    return axios.get('@api/contract/calculator/liquidation_price', { params });
  }
};
export const $common = {
  common_config() {
    return axios.get('@bapi/v2/basic/common_config');
  },
  popUpList(par: any) {
    return axios.get('@uapi/v1/popUp/list', { params: par });
  },
  noMorePop(par: any) {
    return axios.post('@uapi/v1/popUp/noMorePop', par, {
      headers: { 'Content-Type': 'application/json' }
    });
  },

  closePop(par: any) {
    return axios.post('@uapi/v1/popUp/closePop', par, {
      headers: { 'Content-Type': 'application/json' }
    });
  }
};
export const $ex = {
  // k线http
  kline_history(par: any) {
    return axios.get('@api/quote/v1/klines', { params: par });
  },
  depth(par: any) {
    return axios.get('@api/quote/v1/depth', { params: par });
  },
  serverTime(par: any) {
    return axios.get('@api/quote/v1/time', { params: par });
  }
};
export const $assets = {
  // 校验地址是否合法
  valid(data: object) {
    return axios.post('@capi/v1/withdraw/address/valid', data);
  },
  // 地址自动匹配链
  autoMatchChain(params: object) {
    return axios.get('@bapi/v1/withdraw/address/auto-match-chain', { params });
  },
  // 充币记录
  deposit_order_list(par: any) {
    return axios.get('@capi/v1/deposit/order_list', { params: par });
  },
  // 取消提币
  cancel(data: object) {
    return axios.post('@capi/asset/withdrawal/order/cancel', data);
  },
  // 提币记录
  withdrawal_order_list(par: any) {
    return axios.get('@capi/asset/withdrawal/order_list', { params: par });
  },
  // 充币
  balance_deposit(par: any) {
    return axios.get('@capi/v1/deposit/address', { params: par });
  },
  // 充币
  checkUserAddress(par: any) {
    return axios.get('@capi/v1/deposit/checkUserAddress', { params: par });
  },
  // 提币，获取余额，手续费
  quota_info(par: any) {
    return axios.get('@capi/asset/withdrawal/quota_info', { params: par });
  },
  // 提币地址黑名单判断
  withdraw_address_check(par: any) {
    return axios.get('@capi/asset/withdrawal/address/check', { params: par });
  },
  // 提币地址白名单状态查询
  withdrawWhiteStatus(params: any) {
    return axios.get('@bapi/v1/withdraw/get-white-switch', { params });
  },
  // 设置提币地址白名单状态
  // https://www.wcsbapp.com/bapi/v1/withdraw/set-white-switch
  withdrawWhiteStatusSet(params: any) {
    return axios.post('@bapi/v1/withdraw/set-white-switch', params, {
      headers: { 'Content-Type': 'application/json' }
    });
  },
  // https://www.wcsbapp.com/bapi/v1/withdraw/update-address-white
  // 提币地址白名单状态更新
  withdrawUpdateWhiteStatus(params: any) {
    // params to query string
    const queryString = Object.keys(params)
      .map(key => `${key}=${params[key]}`)
      .join('&');
    return axios.post(
      `@bapi/v1/withdraw/update-address-white?${queryString}`,
      params,
      {
        headers: { 'Content-Type': 'application/json' }
      }
    );
  },
  // 获取提币地址
  get_withraw_address(data: any) {
    return axios.get('@capi/v1/withdraw/address_list', { params: data });
  },
  // 提币 step1
  withdraw(data: any) {
    return axios.post('@capi/v1/withdraw/create', data);
  },
  // 站内提币 step1
  innerWithdraw(data: any) {
    return axios.post('@capi/v1/withdraw/createInnerTransfer', data);
  },
  // 提币 step2
  withdraw_verify(data: any) {
    return axios.post('@capi/asset/withdrawal/verify', data);
  },
  // 添加提币地址
  add_withraw_address(data: any) {
    return axios.post('@capi/v1/withdraw/address/add', data);
  },
  del_withraw_address(data: any) {
    return axios.post('@capi/v1/withdraw/address/delete', data);
  },
  // 资产-其他记录（包括划转）
  getOtherRecord(params: any) {
    return axios.get('@api/asset/balance_flow', { params });
  },
  // 资产-划转记录
  getTransferRecord(params: any) {
    return axios.get('@bapi/v1/asset/account_transfer', { params });
  },
  // 子账户资产划转
  subAccountTransfer(params: any) {
    return axios.get('@bapi/v1/asset/account_transfer_v2', { params });
  },
  // 获取用户充值token到账户类型
  getDepositTokenAccount(params: any) {
    return axios.get('@capi/v1/deposit/token/account', { params });
  },
  // 用户充值token到指定账户
  setDepositTokenAccount(data: any) {
    return axios.post('@capi/v1/deposit/token/account', data);
  },
  // 提币 V2
  // /v1/withdraw/create/V2
  withdrawV2(data: any) {
    return axios.post('@capi/v1/withdraw/create/V2', data);
  },
  // 站内提币 V2
  innerWithdrawV2(data: any) {
    return axios.post('@capi/v1/withdraw/createInnerTransfer', data);
  },
  riskBalance() {
    return axios.get('@bapi/v1/account/risk_balance');
  },
  vasps() {
    return axios.get('@capi/v1/compliance/vasps');
  },
  vaspsPre(data: any) {
    return axios.post('@capi/v1/compliance/pre', data);
  },
  // bapi/v1//withdraw/order/pass
  withdrawOrderPass(data: any) {
    return axios.post('@capi/v1/withdraw/order/pass', data);
  },
  // 获取提现订单详情
  withdrawOrderDetail(data: any) {
    return axios.get('@capi/v1/withdraw/order', { params: data });
  },
  // 获取充值未到账申请列表
  getDepositNotReceivedYedList(params: object) {
    return axios.get('@bapi/v1/deposit/getUserAbnormalDepositList', { params });
  },
  // 获取充值未到账申请前检查钱包类型
  getDepositNotReceivedWallet(data: any) {
    return axios.post('@bapi/v1/deposit/userAbnormalDepositApplyCheck', data, {
      headers: { 'Content-Type': 'application/json' }
    });
  },
  // 充值未到账申请
  depositNotReceivedYedApply(data: any) {
    return axios.post('@bapi/v1/deposit/userAbnormalDepositApply', data, {
      headers: { 'Content-Type': 'application/json' }
    });
  },
  // 取消申请
  canceldepositNotReceivedApply(params: object) {
    return axios.get('@bapi/v1/deposit/cancelApply', { params });
  },
  // 获取提币前检查
  getWithdrawPrep(params: any) {
    return axios.get('@capi/v1/withdraw/prep', { params });
  },
  // 获取快捷提币配置
  getQuickWithdrawConfig(params: any) {
    return axios.get('@bapi/v1/withdraw/quick/config', { params });
  },
  // 获取快捷提币开关配置 (开关状态、24小时提币额度、单笔提币额度)
  getQuickWithdrawSwitch() {
    return axios.get('@bapi/v1/withdraw/quick/user-config');
  },
  // 设置快捷提币开关
  setQuickWithdrawSwitch(params: any) {
    return axios.post('@bapi/v1/withdraw/quick/set-switch', params, {
      headers: { 'Content-Type': 'application/json' }
    });
  }
};
export const $download = {
  downloadInfo() {
    return axios.get('@api/basic/download/info', {});
  },

  popUpConfig() {
    return axios.get('@uapi/v1/popUp/config', {});
  }
};

export const $futures = {
  // 获取合约交易对总持仓量
  getFeeRate(params: any) {
    return axios.get('@bapi/v1/basic/trade/fee-rate', { params });
  },
  // 获取合约交易对总持仓量
  getPositionTotal(params: any) {
    return axios.get('@bapi/v1/futures/position/total', { params });
  },
  // 资金费率
  fundingRates(params: any) {
    return axios.get('@bapi/v1/futures/funding_rates', { params });
  },
  // 历史资金费率
  fundingHistory(params: any) {
    return axios.get('@bapi/v1/futures/history_funding_rates', { params });
  },
  // 资金明细
  balanceFlow(params: object) {
    return axios.get('@bapi/v1/futures/asset/balance_flow', {
      params: formatParams(params)
    });
  },
  // 历史委托
  tradeOrders(params: object) {
    return axios.get('@bapi/v1/futures/order/trade_orders', {
      params: formatParams(params)
    });
  },
  // 当前委托
  currentDelegate(params: object) {
    return axios.get('@capi/v1/futures/order/open_orders', {
      params: formatParams(params)
    });
  },
  // 风险基金
  fundingBalance(params: object) {
    return axios.get('@bapi/v1/futures/insurance_funding_balance', { params });
  },
  // 价格指数K线
  indexKlines(params: object) {
    return axios.get('@api/quote/v1/index/klines', { params });
  },
  // 组合
  indexConfig(params: object) {
    return axios.get('@api/quote/v1/index/config', { params });
  },
  // 标记价格K线
  markPriceKlines(params: object) {
    return axios.get('@api/quote/v1/markPrice/klines', { params });
  },
  // 订单详情
  getOrderDetails(params: object) {
    return axios.get('@bapi/v1/futures/order/get', {
      params: formatParams(params)
    });
  },
  // 合约订单详情
  futuresOrderMatchInfo(params: object) {
    return axios.get('@bapi/v1/futures/order/match_info', {
      params: formatParams(params)
    });
  },
  // 成交明细
  tradesDetails(params: object) {
    return axios.get('@bapi/v1/futures/order/my_trades', {
      params: formatParams(params)
    });
  },
  // 合约交易对信息
  futuresSymbolsInfo(params: object) {
    return axios.get('@api/v1/basic/symbols', { params });
  },
  // 永续合约资产列表
  futuresAssetAvailable(params: object) {
    return axios.get('@capi/v1/futures/asset/available', {
      params: formatParams(params)
    });
  },
  // 合约页用资产
  futuresAssetTradeable(params: object) {
    return axios.get('@bapi/v1/futures/asset/tradeable', { params });
  },

  // 获取用户所有合约交易对的杠杆倍数和仓位类型(对象)
  futuresAccountLeverage(params: object) {
    return axios.get('@capi/v1/futures/account/account_leverage', {
      params: formatParams(params)
    });
  },
  // 获取用户所有合约交易对的杠杆倍数和仓位类型
  futuresOrderSetting(params: object) {
    return axios.get('@capi/v1/futures/order/get_order_setting', {
      params: formatParams(params)
    });
  },
  // 获取是否开启下单确认
  getFuturesOrderConfirm(params: object) {
    return axios.get('@bapi/v1/futures/order/order_setting', {
      params: formatParams(params)
    });
  },
  // 创建订单
  createFuturesOrder(data: object) {
    // 区分合约下单页面
    if (window.location.pathname.includes('/lite-perpetual/')) {
      data.trade_channel = 2;
    } else if (
      window.location.pathname.includes('/futures/') ||
      window.location.pathname.includes('/futures-copy/')
    ) {
      data.trade_channel = 1;
    }

    return axios.post('@capi/v1/futures/order/create', formatParams(data));
  },
  // 获取合约仓位
  getPositionList(params: object) {
    return axios.get('@capi/v1/futures/order/position', {
      params: formatParams(params)
    });
  },
  // 获取止盈止损订单
  // 获取止盈止损设置
  getStopProfitLoss(params: object) {
    return axios.get('@capi/v1/futures/order/position/stop_profit_loss/get', {
      params: formatParams(params)
    });
  },
  // 对订单止盈止损
  stopProfitLoss(data: any) {
    return axios.post(
      '@capi/v1/futures/order/position/stop_profit_loss/set',
      formatParams(data)
    );
  },
  // 市价平仓 暂时没用着
  closePromptly(data: any) {
    return axios.post(
      '@capi/v1/futures/order/close_promptly',
      formatParams(data)
    );
  },
  // 一键平仓
  closePromptlyAll(data: any) {
    return axios.post('@capi/v1/futures/order/close_all', formatParams(data));
  },
  // 调整杠杆倍数
  futuresLeverage(data: any) {
    return axios.post(
      '@capi/v1/futures/account/adjust_leverage',
      formatParams(data)
    );
  },
  // 调整仓位模式
  futuresAdjustType(data: any) {
    // const str = `symbol_id=${data.symbol_id}&margin_type=${data.marginType}`;
    return axios.post(
      '@capi/v1/futures/account/adjust_type',
      formatParams(data)
    );
  },
  // 合约撤单
  futuresOrderCancel(data: any) {
    return axios.post('@capi/v1/futures/order/cancel', formatParams(data));
  },
  // 合约全部撤单
  futuresOrderCancelAll(data: any) {
    return axios.post(
      '@capi/v1/futures/order/cancel_order_list',
      formatParams(data)
    );
  },
  // 调整保证金 /asset/modify_margin
  futuresModifyMargin(data: any) {
    return axios.post(
      '@capi/v1/futures/asset/modify_margin',
      formatParams(data)
    );
  },
  // 设置期货下单选项
  setOrderSetting(data: any) {
    return axios.post(
      '@bapi/v1/futures/order/set_order_setting',
      formatParams(data)
    );
  },

  // 合约反手提交数据
  sendFuturesReversPosition(data: any) {
    return axios.post(
      '@capi/v1/futures/order/reverse_position',
      formatParams(data)
    );
  },

  // 查看止盈止损订单接口
  getOrderStopProfitLoss(params: object) {
    return axios.get('@capi/v1/futures/order/stop_profit_loss/get', {
      params: formatParams(params)
    });
  },

  // 修改开仓止盈止损订单接口
  modifyOrderStopProfitLoss(data: any) {
    return axios.post(
      '@capi/v1/futures/order/stop_profit_loss/modify',
      formatParams(data)
    );
  },

  // 修改合仓模式
  setPositionMergeMode(params: any) {
    return axios.post('@capi/v1/futures/account/adjust_mode', params);
  },
  getPositionMergeMode(params: any) {
    return axios.get('@capi/v1/futures/account/position_mode', { params });
  },

  sendAvaiableQuantity(params: any) {
    return axios.get(
      '@capi/v1/futures/order/position/stop_profit_loss/avaiable_quantity',
      { params: formatParams(params) }
    );
  },

  sendStopProfitLossList(params: any) {
    return axios.get('@capi/v1/futures/order/position/stop_profit_loss/list', {
      params: formatParams(params)
    });
  },

  // 修改止盈止损
  stopProfitLossEdit(data: any) {
    return axios.post(
      '@capi/v1/futures/order/position/stop_profit_loss/edit',
      formatParams(data)
    );
  },

  // 查询仓位历史记录
  getPositionHistoryList(params: any) {
    return axios.get('@bapi/v1/futures/order/position/history-list', {
      params: formatParams(params)
    });
  },

  // 用户合约价差保护开关
  futuresSwitchPriceProtection(params: any) {
    return axios.post(
      '@bapi/v1/futures/account/switch_price_protection',
      params
    );
  },
  // 获取用户合约价差保护开关
  getPriceProtection() {
    return axios.get('@bapi/v1/futures/account/price_protection');
  },
  // 带单员请求止盈止损可设数量
  sendLeaderAvaiableQuantity(params: any) {
    return axios.get(
      '@capi/v1/futures/order/copy_trading/leader/stop_profit_loss/avaiable_quantity',
      { params: formatParams(params) }
    );
  },
  // 切换虚拟盘
  demoFuturesSwitch() {
    return axios.post('@bapi/v1/virtual-trade/switch');
  },
  // 虚拟盘申请资产
  demoFuturesApplyAssets() {
    return axios.post('@bapi/v1/virtual-trade/apply-funds');
  },
  // 含有老数据的带单仓位
  leaderOldDataPositions() {
    return axios.get('@bapi/v1/copy-trading/leader/old_data_positions');
  },
  // bapi/v1/copy-trading/get-leader-history-orders
  getLeaderHistoryOrders(params: any) {
    return axios.get('@bapi/v1/copy-trading/get-leader-history-orders', {
      params
    });
  },

  // 期货下单前检查
  getCreateCheck(params: any) {
    return axios.get('@capi/v1/futures/order/create_check', {
      params: formatParams(params)
    });
  },
  // 追加保证金
  autoAddMargin(params: any) {
    return axios.post('@bapi/v1/futures/order/autoAddMargin', params, {
      headers: { 'Content-Type': 'application/json' }
    });
  },
  // 查询追加保证金的仓位
  getBusinessConfig() {
    return axios.get('@bapi/v1/futures/order/getBusinessConfig');
  }
};

// 子账户
export const subApi = {
  // 子账户列表
  // /bapi/v1/user/sub/list
  getSubList(params: any) {
    return axios.get('@bapi/v1/user/sub/list', { params });
  },
  // 创建子账户
  // POST /bapi/v1/user/sub/create
  subCreateAccount(data: any) {
    return axios.post('@bapi/v1/user/sub/create', data);
  },
  // 冻结子账户
  // POST /bapi/v1/user/sub/frozen
  frozenAccount(data: any) {
    return axios.post('@bapi/v1/user/sub/frozen', data);
  },
  // 解冻子账户
  unFrozenAccount(data: any) {
    return axios.post('@bapi/v1/user/sub/unfrozen', data);
  },
  // 删除子账户
  delSubAccount(data: any) {
    return axios.post('@bapi/v1/user/sub/del', data);
  },
  // 更新子账户备注
  editRemarkSubAccount(data: any) {
    return axios.post('@bapi/v1/user/sub/remark', data);
  },
  // 强制登出子账户
  logoutSubAccount(data: any) {
    return axios.post('@bapi/v1/user/sub/logout', data);
  },
  // 获取子资产信息
  // GET /bapi/v1/asset/sub_user_info
  getSubUserInfo(params: any) {
    return axios.get('@bapi/v1/asset/sub_user_info', { params });
  },

  // 查询apikey可配置权限列表
  // https://www.wcsbapp.com/bapi/v1/user/api-key/auth/list
  getApiKeyAuthList(params: any) {
    return axios.get('@bapi/v1/user/api-key/auth/list', {
      params: formatParams(params)
    });
  },
  // 创建api
  // POST /bapi/v1/user/api-key
  createApiKey(data: any) {
    return axios.post('@bapi/v1/user/api-key', formatParams(data));
  },
  // 获取api列表
  getApiKeyList(params: any) {
    return axios.get('@bapi/v1/user/api-keys', {
      params: formatParams(params)
    });
  },
  // 获取api列表
  getApiKeyDetails(params: any) {
    return axios.get('@bapi/v1/user/api-key/' + params.id, { params });
  },
  // 删除API
  // POST /bapi/v1/user/api-key/delete/{id}
  deleteApiKey(data: any) {
    return axios.post('@bapi/v1/user/api-key/delete/' + data.id, data);
  },
  // 更新api状态
  // POST /bapi/v1/user/api-key/change-status/{id}
  changeApiKeyStatus(data: any) {
    return axios.post('@bapi/v1/user/api-key/change-status/' + data.id, data);
  },
  // 更新Ip
  // POST /bapi/v1/user/api-key/update-ips/{id}
  updateIp(data: any) {
    return axios.post('@bapi/v1/user/api-key/update-ips/' + data.id, data);
  },
  // 编辑api
  // POST /bapi/v1/user/api-key/update
  updateApiKey(data: any) {
    return axios.post('@bapi/v1/user/api-key/update', data);
  }
};

export const cardApi = {
  // 卡劵列表
  getActivitiesExperience(params: any) {
    // return axios.get('@bapi/v1/activities/experience', { params });
    return axios.get('@bapi/v1/activities/voucher', { params });
  },

  // 领取现金券
  // voucherTake(params: any) {
  //   return axios.post('@bapi/voucher/v1/voucher_take', params, {
  //     headers: { 'Content-Type': 'application/json' }
  //   });
  // }

  voucherTake(params: any) {
    return axios.post('@bapi/voucher/v1/voucher_take', params, {
      headers: { 'Content-Type': 'application/json' }
    });
  },

  // 卡券解锁列表
  getVoucherUnlockList(params: any) {
    return axios.get('@bapi/v1/activities/unlockList', { params });
  }
};
// 活动
export const activityApi = {
  // 活动任务总进度条
  activityProgress(params: any) {
    return axios.get('@bapi/v2/welfare/activityProgress', { params });
  },
  // 240U大礼包
  experience_card_info(params: any) {
    return axios.get('@bapi/v1/activities/experience_card_info', { params });
  },
  // 我的任务
  // /bapi/v1/welfare/myTask
  getMyTask(params: any) {
    return axios.get('@bapi/v1/welfare/myTask', { params });
  },
  // 已完成任务列表
  // /bapi/v1/welfare/finishTaskList
  getFinishTaskList(params: any) {
    return axios.get('@bapi/v1/welfare/finishTaskList', { params });
  },
  // 空投列表
  // /bapi/v1/welfare/airdropRewardList
  getAirdropRewardList(params: any) {
    return axios.get('@bapi/v1/welfare/airdropRewardList', { params });
  },

  // 福利中心 二期 v2 接口
  // 进行中的任务 /bapi/v2/welfare/progressTask
  getProgressTask(params: any) {
    return axios.get('@bapi/v2/welfare/progressTask', { params });
  },
  // 完成任务 /bapi/v2/welfare/finishTaskList
  getFinishTask(params: any) {
    return axios.get('@bapi/v2/welfare/finishTaskList', { params });
  },

  // 是否新手用户 /bapi/v2/welfare/isNovice
  getIsNovice(params: any) {
    return axios.get('@bapi/v2/welfare/isNovice', { params });
  },
  // 新手任务 /bapi/v2/welfare/noviceTask
  getNoviceTask(params: any) {
    return axios.get('@bapi/v2/welfare/noviceTask', { params });
  },
  // 常规任务 /bapi/v2/welfare/regularTask
  getRegularTask(params: any) {
    return axios.get('@bapi/v2/welfare/regularTask', { params });
  },
  // 根据邀请码查询任务信息
  getNoviceTaskByInviteCode(params: any) {
    return axios.get('@bapi/v2/welfare/noviceTaskByInviteCode', { params });
  },

  // 我的任务-已结束 /bapi/v1/welfare/myEndActivity
  getMyEndActivity(params: any) {
    return axios.get('@bapi/v1/welfare/myEndActivity', { params });
  },
  // 我的任务-进行中 /bapi/v1/welfare/myOngoingActivity
  getMyOngoingActivity(params: any) {
    return axios.get('@bapi/v1/welfare/myOngoingActivity', { params });
  },
  // 获取活动banner
  getActivityBanners(params?: any) {
    return axios.get('@bapi/v1/my-activity/banner', { params });
  },
  // 获取活动类型
  getMyActivityTypes(params?: any) {
    return axios.get('@bapi/v1/my-activity/types', { params });
  },
  // 获取活动列表
  getMyActivityList(params?: any) {
    return axios.get('@bapi/v1/my-activity/list', { params });
  },

  // /bapi/v1/lottery/winRotation
  getWinRotation(params: any) {
    return axios.get('@bapi/v1/lottery/winRotation', { params });
  },
  // 用户抽奖信息
  getLotteryWinnerApi(params: any) {
    return axios.get('@bapi/v1/lottery/getLotteryWinner', { params });
  },
  // /bapi/v1/lottery/lottery
  lotteryPrize(data: any) {
    return axios.post('@bapi/v1/lottery/lottery', data);
  },
  // 抽奖记录
  getLotteryRecordList(params: any) {
    return axios.get('@bapi/v1/lottery/lotteryRecordList', { params });
  },
  // 是否显示新手tab bapi/v2/welfare/isDisplayNovice
  getIsDisplayNovice(params: any) {
    return axios.get('@bapi/v2/welfare/isDisplayNovice', { params });
  },
  // /bapi/v2/welfare/taskProgress 获取任务进度

  // 获取任务进度
  getTaskProgress(params: any) {
    return axios.get('@bapi/v2/welfare/taskProgress', { params });
  },

  // 圣诞活动
  getActivityWelfare(params: any) {
    return axios.get('@bapi/v1/welfare/getActivity', { params });
  },
  // 圣诞活动-报名
  // /bapi/v1/welfare/activitySign
  activitySign(data: any) {
    return axios.post('@bapi/v1/welfare/activitySign', data);
  },
  // 个人冠军赛排行信息
  getChristmasTradRank(params: any) {
    return axios.get('@bapi/v1/welfare/christmas/individualChampionshipRank', {
      params
    });
  },
  // PNL 排行
  getPnlRank(params: any) {
    return axios.get('@bapi/v1/welfare/christmas/pnlRank', { params });
  },
  // 获取圣诞活动信息
  // /bapi/v1/welfare/christmas/main
  getChristmasMain(params: any) {
    return axios.get('@bapi/v1/welfare/christmas/main', { params });
  },
  getActivityWinRotation(params: any) {
    return axios.get('@bapi/v1/lottery/activityWinRotation', { params });
  },

  // 圣诞二期活动报名
  christmasSign(data: any) {
    return axios.post('@bapi/v1/welfare/sign', data);
  },
  christmasSignInfo(params: any) {
    return axios.get('@bapi/v1/welfare/signInfo', { params });
  },
  // 获取活动轮播数据
  getCarousel(params: any) {
    return axios.get('@bapi/v1/welfare/carousel', { params });
  },
  getMySignActivity(params: any) {
    return axios.get('@bapi/v1/welfare/getMySignActivity', { params });
  },

  // 龙年活动 获取 排名
  // /bapi/v1/welfare/futures/trade/rank
  getDragonTradeRank(params: any) {
    return axios.get('@bapi/v1/welfare/futures/trade/rank', { params });
  },
  // 活动是否报名
  getIsSign(params: any) {
    return axios.get('@bapi/v1/welfare/isSign', { params });
  },
  // 获取活动信息
  // /bapi/v1/welfare/futures/trade/mainInfo
  getDragonMainInfo(params: any) {
    return axios.get('@bapi/v1/welfare/futures/trade/mainInfo', { params });
  },
  // 龙年活动每日乐透中奖结果
  getDailyWinUser(params: any) {
    return axios.get('@bapi/v1/dailyLotto/dailyWinUser', { params });
  },
  // 龙年活动每日乐透 中奖结果
  getDailyLottoRecords(params: any) {
    return axios.get('@bapi/v1/dailyLotto/dailyLottoRecords', { params });
  },
  // 龙年活动每日乐透 入金金额
  getDailyDepositAmount(params: any) {
    return axios.get('@bapi/v1/dailyLotto/getDailyDepositAmount', { params });
  },
  // 龙年活动每日乐透 抽奖结果
  getMyDailyWin(params?: any) {
    return axios.get('@bapi/v1/dailyLotto/myDailyWin', { params });
  },
  // 龙年活动每日乐透 历史中奖记录
  getDailyLottoWinRecords(params: any) {
    return axios.get('@bapi/v1/dailyLotto/winRecords', { params });
  },
  // v1/welfare/batchSign
  batchSign(data: any) {
    return axios.post('@bapi/v1/welfare/batchSign', data);
  },
  getSignCondition(params: any) {
    return axios.get('@bapi/v1/welfare/signCondition', { params });
  },
  activityLeaderSign(data: any) {
    return axios.post('@bapi/v1/welfare/activityLeaderSign', data);
  },
  noticeLeader(params: any) {
    return axios.get('@bapi/v1/welfare/noticeLeader', { params });
  },
  getTeamSignInfo(params: any) {
    return axios.get('@bapi/v1/welfare/teamSignInfo', { params });
  },
  // 战队赛数据
  getTeamMainInfo(params: any) {
    return axios.get('@bapi/v1/welfare/futures/trade/mainInfo', { params });
  },
  getTeamRank(params: any) {
    return axios.get('@bapi/v1/welfare/teamRank', { params });
  },
  // 任务报名
  taskSign(data: any) {
    return axios.post('@bapi/v1/welfare/taskSign', data);
  },
  // 获取任务报名列表
  taskSignInfo(params: any) {
    return axios.get('@bapi/v1/welfare/signInfo', { params });
  },
  // 完成新手任务弹窗
  getRewardNovicePop(params: any) {
    return axios.get('@bapi/v1/welfare/getRewardPop', { params });
  }
};
// 代理人
export const agentApi = {
  // 代理人公告
  getAnnouncement(data: any) {
    return axios.post('@bapi/v1/agent/home/bulletin/queryList', data, {
      headers: { 'Content-Type': 'application/json' }
    });
  },
  // 代理人首页Dashboard
  getAgentDashboard(params: any) {
    return axios.get('@bapi/v1/agent/home/dashboard', { params });
  },

  // 用户排名
  getAgentUserRanking(params: any) {
    return axios.get('@bapi/v1/agent/home/dashboard/userRanking', { params });
  },

  // 子代理排名
  getAgentAgentRanking(params: any) {
    return axios.get('@bapi/v1/agent/home/dashboard/agentRanking', { params });
  },

  // 代理人查询默认返佣配置
  getAgentBonusInfo(params: any = '') {
    return axios.get('@bapi/v1/agent/bonus/info', { params });
  },

  // 查询所有邀请码
  getAgentBonusList(params: any) {
    return axios.get('@bapi/v1/agent/bonus/list', { params });
  },

  // 创建邀请码
  createAgentBonus(params: any) {
    return axios.post('@bapi/v1/agent/bonus/add', params, {
      headers: { 'Content-Type': 'application/json' }
    });
  },

  // 查询邀请码统计数据
  getAgentBonusStatisticsList(params: any) {
    return axios.get('@bapi/v1/agent/bonus/statistics/list', { params });
  },

  // 查询子代理人列表
  getAgentQueryBroker(params: any) {
    return axios.get('@bapi/agent/query_broker', { params });
  },

  // 分佣收入-汇总列表
  getAgentCommissionInfo(params: any) {
    return axios.get('@bapi/v1/agent/commission/bonus/info', { params });
  },

  // 分佣收入-明细列表
  getAgentCommissionList(params: any) {
    return axios.get('@bapi/v1/agent/commission/bonus/list', { params });
  },

  // 我的客户列表查询,代理人首页使用
  getDirectList(params: any) {
    return axios.get('@bapi/v1/invitation/direct-customer/list', { params });
  },

  // 我的客户列表查询
  getUserManageList(params: any) {
    return axios.get('@bapi/v1/agent/user-manage/list', { params });
  },

  // 用户福利列表查询
  getBenefitList(params: any) {
    return axios.get('@bapi/v1/agent/user-manage/benefit/list', { params });
  },

  // 设置用户福利
  setUserBenefit(params: any) {
    return axios.post(
      '@bapi/v1/agent/user-manage/benefit/setUserBenefit',
      params
    );
  },

  // 设置用户福利影响人数
  queryUserManageBenefitUserCount(params: any) {
    return axios.post(
      '@bapi/v1/agent/user-manage/benefit/queryUserManageBenefitUserCount',
      params
    );
  },

  // 出入金汇总
  getDepositWithdrawSummary(params: any) {
    return axios.get('@bapi/v1/agent/depositWithdraw/summary', { params });
  },

  // 出入金明细列表查询
  getDepositWithdrawDetailList(params: any) {
    return axios.get('@bapi/v1/agent/depositWithdraw/detail/list', { params });
  },

  // 我的客户-dashboard
  getDirectAggregation(params: any) {
    return axios.get('@bapi/v1/invitation/direct-customer/aggregation', {
      params
    });
  },

  // 数据表现-子代理列表查询
  // getSubAgentList(params: any) {
  //   return axios.get('@bapi/v1/agent/sub-agent-data/list', { params });
  // },

  // 数据表现-子代理列表查询
  getSubAgentList(params: any) {
    return axios.get('@bapi/v1/agent/sub-agent-data/list_page', { params });
  },

  // 数据表现-子代理-dashboard
  getSubAgentAggregation(params: any) {
    return axios.get('@bapi/v1/agent/sub-agent-data/dashboard', { params });
  },

  // 数据表现-活动数据列表查询
  getActivityList(params: any) {
    return axios.get('@bapi/v1/agent/activity-data/list', { params });
  },

  // 数据表现-活动数据-dashboard
  getActivityAggregation(params: any) {
    return axios.get('@bapi/v1/agent/market-activity/dashboard', { params });
  },

  // 活动查询
  getActivity(params: any) {
    return axios.get('@bapi/v1/activities', { params });
  },

  // 创建/升级代理人
  createAgentAddSubAgent(params: any) {
    return axios.post('@bapi/agent/add_sub_agent', params);
  },

  // 重新申请代理人
  agentReaddSubAgent(params: any) {
    return axios.post('@bapi/agent/readd_sub_agent', params);
  },

  // 修改代理人
  agentUpdateSubAgent(params: any) {
    const id = params.id;
    delete params.id;
    return axios.post(`@bapi/v1/agent/update_sub_agent/${id}`, params);
  },

  // BD代理人UID及下级链路查询
  getBdCheckQueryUid(params: any) {
    return axios.get('@bapi/v1/agent/bd/check_query_uid', { params });
  },

  // 首页banner查询
  getAgentBanners() {
    return axios.get('@bapi/v1/agent/home/banners');
  },

  // 活动banner查询
  getAgentActivityBanner(params: any) {
    return axios.get('@bapi/v1/agent/home/banner', { params });
  },

  // 子代理返佣k线
  getAgentSubAgentLine(params: any) {
    return axios.get('@bapi/v1/agent/sub-agent/bonus/line', { params });
  },

  // 直客返佣k线
  getAgentDirectCustomerLine(params: any) {
    return axios.get('@bapi/v1/agent/direct-customer/bonus/line', { params });
  },

  // 交易量
  getAgentTradeAmountLine(params: any) {
    return axios.get('@bapi/v1/agent/trade-amount/line', { params });
  },

  // 1 交易量 2返佣 3入金量 4净手续费 合并接口
  getAgentOverview(params: any) {
    return axios.get('@bapi/v1/agent/over-view/line', { params });
  },

  // 1 交易人数 2入金人数 3出金任务 4注册人人数 5首次交易人数
  getTradeCount(params: any) {
    return axios.get('@bapi/v1/agent/trade-count/line', { params });
  },

  // 代理人配置接口
  getAgentConfig(params: any = '') {
    return axios.get('@bapi/v1/agent/config', { params });
  },

  // 设置默认邀请码
  setAgentBonusDefault(params: any) {
    return axios.post(`@bapi/v1/agent/bonus/update-default/${params.id}`, {
      headers: { 'Content-Type': 'application/json' }
    });
  },

  // 修改邀请码配置
  updateAgentBonus(params: any) {
    const id = params.id;
    delete params.id;
    return axios.post(`@bapi/v1/agent/bonus/update/${id}`, params, {
      headers: { 'Content-Type': 'application/json' }
    });
  },

  // 解绑代理人
  cancelSubAgent(params: any) {
    return axios.post('@bapi/agent/cancel_sub_agent', params);
  },

  // 代理人查询详情
  getAgentReaddSub(params: any) {
    return axios.get('@bapi/agent/info', { params });
  },

  // 代理人获取邀请码
  getAgentGenerate() {
    return axios.get('@bapi/v1/agent/bonus/generate');
  },

  // 获取所有活动
  getAgentActivityList() {
    return axios.get('@bapi/v1/agent/activity/list');
  },

  // 历史活动数据
  getAgentMarketActivityList(params: any) {
    return axios.get('@bapi/v1/agent/market-activity/list', { params });
  },

  // 设置活动专属链接
  marketActivityConfigLink(params: any) {
    return axios.post('@bapi/v1/agent/market-activity/config-link', params, {
      headers: { 'Content-Type': 'application/json' }
    });
  },

  // 活动tab
  getAgentActivityTab() {
    return axios.get('@bapi/v1/agent/activity/tab');
  },

  // 活动详情
  getAgentActivityDetail(params: any) {
    return axios.get('@bapi/v1/agent/activity/detail', { params });
  },

  // 查询代理人短链跳转路径
  getAgentBonusJumpPath(params: any) {
    return axios.get('@bapi/v1/agent/bonus/jump-path', { params });
  },

  // 下载列表查询
  getAgentDownList(params: any) {
    return axios.get('@bapi/v1/agent/down/list', { params });
  },

  // 导出生成excel
  agentGenerateExcel(data: any) {
    return axios.post('@bapi/v1/agent/generate-excel', data, {
      headers: { 'Content-Type': 'application/json' }
    });
  },

  // 取消下载
  agentCancelDown(data: any) {
    return axios.post('@bapi/v1/agent/cancel-down', data, {
      headers: { 'Content-Type': 'application/json' }
    });
  },

  // 修改备注
  updateDirectCustomer(data: any) {
    return axios.post('@bapi/v1/invitation/direct-customer/update', data, {
      headers: { 'Content-Type': 'application/json' }
    });
  },
  // 代理人查询下级持仓
  getAgentPosition(params: any) {
    return axios.get('@bapi/v1/agent/position', { params });
  },
  // 根据id查询短链详情
  getAgentInviteLinkInfo(params: any) {
    return axios.get('@bapi/v1/agent/invite-link/info', { params });
  },
  // 代理人查询下级历史委托
  getAgentTradeOrderHistory(params: any) {
    return axios.get('@bapi/v1/agent/order_history', { params });
  },

  // 子代理-撤销申请
  agentSubAgentCancel(data: any) {
    return axios.post(
      `@bapi/v1/agent/sub-agent/cancel/${data.userId}`,
      {},
      {
        headers: { 'Content-Type': 'application/json' }
      }
    );
  },

  // 代理人激励奖励信息
  getAgentInspireInfo() {
    return axios.get('@bapi/v1/agent/v2/inspire-info');
  },

  // 未读活动数量
  getAgentActivityUnreadCnt() {
    return axios.get('@bapi/v1/agent/activity/unread-cnt');
  },

  // 子代理-撤销申请
  agentActivityRead(data: any) {
    return axios.post('@bapi/v1/agent/activity/read', data, {
      headers: { 'Content-Type': 'application/json' }
    });
  },

  // 代理人返佣概览
  getAgentCommissionDetail(params: any) {
    return axios.get('@bapi/v1/agent/home/commissionDetail', { params });
  },

  // 代理人返佣概览信息
  getAgentHomeCommissionInfo(params: any) {
    return axios.get('@bapi/v1/agent/home/commissionInfo', { params });
  },

  // 代理人概览信息
  getAgentHomeDataView(params: any) {
    return axios.get('@bapi/v1/agent/home/data-view', { params });
  },

  // 代理人现货订单历史
  getAgentSpotOrderHistory(params: any) {
    return axios.get('@bapi/v1/agent/spot_order_history', { params });
  },

  // 出入金报警列表
  getDepositWithdrawAlarmList(params: any) {
    return axios.get('@bapi/v1/agent/depositWithdraw/alarm/list', { params });
  },

  // 返佣比例增加后弹窗更新状态
  updateAgentPopUp() {
    return axios.post('@bapi/v1/agent/pop-up');
  },

  // 返佣比例增加后弹窗
  getAgentPopUp() {
    return axios.get('@bapi/v1/agent/pop-up');
  },

  // 波斯代理人一周年活动
  getAgentFirstAnniversary(params: any) {
    return axios.get('@bapi/v1/agent/first-anniversary', { params });
  },

  // 代理变动记录
  getAgentChangeBind(params: any) {
    return axios.get('@bapi/v1/agent/agent_change_bind', { params });
  }
};

export const affiliates = {
  // 获取当前IP对应的国家
  getCurrentCountry() {
    return axios.get('@bapi/v1/basic/country/current_ip');
  },
  apply(data: any) {
    return axios.post('@bapi/v1/agent/apply', data, {
      headers: { 'Content-Type': 'application/json' }
    });
  },
  // 邮箱检验
  validateEmail(data: any) {
    return axios.post('@bapi/v1/agent/apply/check', data, {
      headers: { 'Content-Type': 'application/json' }
    });
  },
  // 专属活动查询
  getExclusiveActivity(params: any) {
    return axios.get('@bapi/v1/agent/exclusive-activity', { params });
  },
  // 代理人开仓活动
  getAgentOpenPositionActivity(params: any = '') {
    return axios.get('@bapi/v1/agent/open-position-activity', { params });
  }
};
// vip
export const vipApi = {
  levelConfigs(params: any) {
    return axios.get('@bapi/v1/user/level-info', { params });
  },
  feeConfig(params: any) {
    return axios.get('@bapi/b1/broker/fee/config', { params });
  },
  // 特殊费率列表
  getFeeRateInfo() {
    return axios.get('@bapi/v1/user/fee-rate/info');
  },
  // 提币费用列表
  getWithdrawFeeList(params: any) {
    return axios.get('@bapi/v1//withdraw/feeInfo', { params });
  }
};

// ip黑名单
export const ip = {
  ipBlackList() {
    return axios.get('@bapi/v1/basic/ip-black-list');
  },
  checkAddress(params: any) {
    return axios.get('@uapi/v1/official_channel/checkAddress', { params });
  }
};
export const copyTrading = {
  // 转账记录
  subAccountTransfer(params: any) {
    return axios.get('@bapi/v1/copy-trading/leader-transfer-history', {
      params
    });
  },

  // 带单员申请条件
  applyForLeaderCondition(params: any) {
    return axios.get('@api/v1/copy-trading/applyForLeaderCondition', {
      params
    });
  },
  // 带单员申请接口
  applyForLeader(data: any) {
    return axios.post('@api/v1/copy-trading/applyForLeader', data, {
      headers: { 'Content-Type': 'application/json' }
    });
  },

  // 简介翻译
  translationSummary(params: any) {
    return axios.get('@api/v1/copy-trading/translation/summary', { params });
  },

  // 交易员列表
  getLeaders(params: any) {
    return axios.get('@bapi/v1/copy-trading/leaders-new', { params });
    // return axios.get('@mock/api/v1/copy-trading/leaders', { params });
  },
  // /bapi/v1/copy-trading/post-leaders-new
  getLeadersPost(data: any) {
    return axios.post('@bapi/v1/copy-trading/post-leaders-new', data, {
      headers: { 'Content-Type': 'application/json' }
    });
  },
  // 交易员详情（客态）
  getLeaderInfo(params: any) {
    return axios.get('@bapi/v1/copy-trading/leader-detail', { params });
    // return axios.get('@mock/api/v1/copy-trading/leader-detail', { params });
  },
  // 交易员详情（主态）
  getLeaderSelfInfo(params: any) {
    return axios.get('@bapi/v1/copy-trading/my-lead-detail', { params });
    // return axios.get('@mock/api/v1/copy-trading/my-lead-detail', { params });
  },
  // 我的跟单详情
  getFollowInfo(params: any) {
    return axios.get('@bapi/v1/copy-trading/my-follow-detail', { params });
    // return axios.get('@mock/api/v1/copy-trading/my-follow-detail', { params });
  },
  // 我的跟单设置
  getMyFollowConfig(params: any) {
    return axios.get('@bapi/v1/copy-trading/my-follow-config', { params });
  },

  // 是否为交易员
  getCheckLeader(params: any) {
    return axios.get('@bapi/v1/copy-trading/check-leader', { params });
  },

  // 当前带单
  getCurrentLeadOrders(params: any) {
    return axios.get('@bapi/v1/copy-trading/current-lead-orders', { params });
  },
  // 历史带单
  getHistoryLeadOrders(params: any) {
    return axios.get('@bapi/v1/copy-trading/history-lead-orders', { params });
  },
  // 跟单失败记录
  getFailureRecords(params: any) {
    return axios.get('@bapi/v1/copy-trading/failure-records', { params });
  },
  // 跟单0滑点交易对
  getZeroSlippageSymbols(params: any) {
    return axios.get('@bapi/v1/copy-trading/zero-slippage-symbols', { params });
  },
  // 客态 跟随者
  getFollowers(params: any) {
    return axios.get('@bapi/v1/copy-trading/top-followers', { params });
  },
  // 主态 分润
  getMySharingProfit(params: any) {
    return axios.get('@bapi/v1/copy-trading/my-sharing-profit', { params });
  },
  // 主态 我的跟单用户
  getMyCurrentFollowers(params: any) {
    return axios.get('@bapi/v1/copy-trading/my-current-followers', { params });
  },
  // 主态 我的跟单用户
  removeMyFollower(params: any) {
    return axios.post('@bapi/v1/copy-trading/my-follower/remove', params, {
      headers: { 'Content-Type': 'application/json' }
    });
  },
  // 我的交易员
  getMyCurrentLeader(params: any) {
    return axios.get('@bapi/v1/copy-trading/my-current-leader', { params });
  },
  // 当前跟单仓位
  getCurrentFollowPositions(params: any) {
    return axios.get('@bapi/v1/copy-trading/current-follow-positions', {
      params
    });
  },
  // 历史跟单
  getHistoryFollowOrder(params: any) {
    return axios.get('@bapi/v1/copy-trading/history-follow-orders', { params });
  },
  // /api/v1/copy-trading/cancel-follow
  // 取消跟随
  cancelFollow(data: any) {
    return axios.post('@bapi/v1/copy-trading/cancel-follow', data, {
      headers: { 'Content-Type': 'application/json' }
    });
  },
  // 取消跟随 前置查询 是否允许取消
  hasFollowingOrder(params: any) {
    return axios.get('@bapi/v1/copy-trading/has-following-order', { params });
  },

  followCopyTrading(params: any) {
    return axios.post('@bapi/v1/copy-trading/follow', params, {
      headers: { 'Content-Type': 'application/json' }
    });
  },
  closePosition(params: any) {
    return axios.post('@bapi/v1/copy-trading/close-position', params, {
      headers: { 'Content-Type': 'application/json' }
    });
  },
  // 跟单活动时间
  activitiesInfo(params: any) {
    return axios.get('@bapi/v1/activities/info', { params });
  },

  // 查询带单员当前top收益仓位
  getLeaderTopPositions(params: any) {
    return axios.get('@bapi/v1/copy-trading/get-leader-top-positions', {
      params
    });
  },

  // 复制跟单
  copyTradingFollowOrder(params: any) {
    return axios.post('@bapi/v1/copy-trading/follow-order', params, {
      headers: { 'Content-Type': 'application/json' }
    });
  },

  // 获取跟单账户资产信息
  getFollowerAssetInfo(params: any) {
    return axios.get('@bapi/v1/copy-trading/get-follower-asset', { params });
  },
  // 根据订单平仓
  closeByOrder(data: any) {
    return axios.post('@bapi/v1/copy-trading/close-by-order', data, {
      headers: { 'Content-Type': 'application/json' }
    });
  },

  setCopyTradingStopProfitLoss(data: any) {
    return axios.post('@bapi/v1/copy-trading/stop_profit_loss/set', data, {
      headers: { 'Content-Type': 'application/json' }
    });
  },

  // 获取带单员交易数据
  getLeaderTradeDetail(params: any) {
    return axios.get('@bapi/v1/copy-trading/get-leader-trade-detail', {
      params
    });
  },
  // 获取带单员交易币对
  getLeaderTradeSymbols(params: any) {
    return axios.get('@bapi/v1/copy-trading/get-leader-trade-symbols', {
      params
    });
  },
  // 获取带单员交平均收益率
  getLeaderTradeProfit(params: any) {
    return axios.get('@bapi/v1/copy-trading/get-leader-trade-profit', {
      params
    });
  },
  // 获取跟单员交平均收益率
  getfollowerTradeProfit(params: any) {
    return axios.get('@bapi/v1/copy-trading/follower-profit-ratios', {
      params
    });
  },
  // 获取带单员跟随者总收益
  getLeaderTradeFollowProfit(params: any) {
    return axios.get('@bapi/v1/copy-trading/get-leader-trade-follow-profit', {
      params
    });
  },

  // 查询交易员累计收益折线图
  getLeaderTradeAccumulateProfit(params: any) {
    return axios.get(
      '@bapi/v1/copy-trading/get-leader-trade-accumulate-profit',
      { params }
    );
  },

  // 查询交易员雷达图
  getLeaderRadarChart(params: any) {
    return axios.get('@bapi/v1/copy-trading/get-leader-radar-chart', {
      params
    });
  },
  // 获取带单员交易种类
  getLeaderTradeSymbolType(params: any) {
    return axios.get('@bapi/v1/copy-trading/get-leader-trade-symbol-type', {
      params
    });
  },
  // 获取带单员交持仓时间
  getLeaderTradeOrderDuration(params: any) {
    return axios.get('@bapi/v1/copy-trading/leader-periods', { params });
  },
  // 查询交易大师列表 - 图表
  getIdentityTypeLeaders(params: any) {
    return axios.get('@bapi/v1/copy-trading/identity-type-leaders', { params });
  },

  // 获取 取消跟随 - 详情
  getCancelFollowDetails(params: any) {
    return axios.get('@bapi/v1/copy-trading/cancel-follow-details', { params });
  },

  setCopyTradingReversePosition(data: any) {
    return axios.post('@bapi/v1/copy-trading/reverse_position', data);
  },

  setCopyTradingCloseByPosition(data: any) {
    return axios.post('@bapi/v1/copy-trading/close-by-positions', data, {
      headers: { 'Content-Type': 'application/json' }
    });
  },

  // 跟单，检查邀请码
  setInviteFollowCheck(data: any) {
    return axios.post('@bapi/v1/copy-trading/invite_follow_check', data);
  },
  // 带单员设置
  setLeaderConfig(data: any) {
    return axios.post('@bapi/v1/copy-trading/leader-config/set', data, {
      headers: { 'Content-Type': 'application/json' }
    });
  },
  // BANNER
  bannerList(data: any) {
    return axios.get('@bapi/v1/copy-trading/leader/banner-list', data);
  },
  // /copy-trading/get-modify-margin
  getModifyMargin(params: any) {
    return axios.get('@bapi/v1/copy-trading/get-modify-margin', { params });
  },
  setModifyMargin(data: any) {
    return axios.post('@bapi/v1/copy-trading/modify-margin', data, {
      headers: { 'Content-Type': 'application/json' }
    });
  },

  ranking(params: any) {
    return axios.get('@bapi/v1/copy-trading/ranking', { params });
  }
};

export const fiatApi = {
  // 获取支持法币
  getFiatCurrency() {
    return axios.get('@bapi/v1/fiat/basic/fiat-currency');
  },

  // 获取支持加密币
  getCryptoCurrency() {
    return axios.get('@bapi/v2/basic/crypto-currency');
  },

  // 获取所有渠道
  getFiatChannels() {
    return axios.get('@bapi/v1/fiat/basic/channels');
  },

  // 查询订单
  getFiatOrders(params: any) {
    return axios.get('@bapi/v1/fiat/orders', { params });
  },

  // 批量报价
  getFiatBatchQuote(params: any) {
    return axios.get('@bapi/v1/fiat/batch-quote', { params });
  },

  // 下单
  createFiatOrder(data: any) {
    return axios.post('@bapi/v1/fiat/create-order', data, {
      headers: { 'Content-Type': 'application/json' }
    });
  }
};
export const halloween = {
  contract_trade_rank() {
    return axios.get('@bapi/v1/activities/contract_trade_rank?');
  }
};

// pixel 限时大礼包活动
export const pixelApi = {
  // 邮箱注册
  emailRegister(data: object) {
    return axios.post('@capi/user/email_sign_up', data, { crypto: true });
  },
  // 手机注册
  mobileRegister(data: object) {
    return axios.post('@capi/user/mobile_sign_up', data, { crypto: true });
  },
  // pixel 活动 判断是否绑定gameId
  getGameRelation(data: any) {
    return axios.post('@bapi/v1/welfare/game/taskStatus', data, {
      headers: { 'Content-Type': 'application/json' }
    });
  }
};

export const noticeAPI = {
  // 拉取顶飘
  pullNotice() {
    return axios.get('@uapi/v1/float-notice/pull');
  },
  // 公告分类
  getNoticeGroup() {
    return axios.get('@uapi/v1/float-notice/classification/list');
  },
  // 公告列表
  getNoticeList(params: object) {
    return axios.get('@uapi/v1/float-notice/list', { params });
  },
  // 设置已读状态
  setRead(data: object) {
    return axios.post('@uapi/v1/float-notice/read', data, {
      headers: { 'Content-Type': 'application/json' }
    });
  },
  // 首页faq
  queryFaq(params: object) {
    return axios.get('@uapi/v1/faq/common/query_faq', { params });
  }
};

// 合约策略
export const strategyAPI = {
  // 网格详情
  gridDetail(params: object) {
    return axios.get('@bapi/v1/futures-strategy/strategy/grid-detail', {
      params
    });
  },
  // 网格收益详情
  gridProfitDetail(params: object) {
    return axios.get('@bapi/v1/futures-strategy/strategy/grid-profit-detail', {
      params
    });
  },
  // 网格成交记录
  gridOrderRecord(params: object) {
    return axios.get('@bapi/v1/futures-strategy/strategy/gridRecord', {
      params
    });
  },
  // 网格成交记录详情
  gridOrderRecordDetail(params: object) {
    return axios.get('@bapi/v1/futures-strategy/strategy/gridRecordDetail', {
      params
    });
  },
  // 马丁格尔详情
  martingaleDetail(params: object) {
    return axios.get('@bapi/v1/futures-strategy/strategy/martingale-detail', {
      params
    });
  },
  // 马丁格尔收益详情
  martingaleProfitDetail(params: object) {
    return axios.get(
      '@bapi/v1/futures-strategy/strategy/martingale-profit-detail',
      { params }
    );
  },
  // 马丁格尔成交记录
  martingaleRecord(params: object) {
    return axios.get('@bapi/v1/futures-strategy/strategy/martingaleRecord', {
      params
    });
  },
  // 马丁格尔成交记录详情
  martingaleRecordDetail(params: object) {
    return axios.get(
      '@bapi/v1/futures-strategy/strategy/martingaleRecordDetail',
      { params }
    );
  },
  // 撤销手动加仓单
  cancelManuallyAddOrder(data: object) {
    return axios.post(
      '@bapi/v1/futures-strategy/cancelManuallyAddOrder',
      data,
      {
        headers: { 'Content-Type': 'application/json' }
      }
    );
  },
  // 合约策略全局配置
  strategyConfig() {
    return axios.get('@bapi/v1/strategy/config/futures');
  },

  // 爆仓价预估计算 - 网格
  algoLiquidationPriceGrid(params: any) {
    return axios.get(
      '@bapi/v1/strategy-trade/algo/liquidation-price/grid/cal',
      { params }
    );
  },

  // ai策略推荐
  getAlgoRecommendation(params: any) {
    return axios.get('@bapi/v1/strategy-trade/algo/recommendation', { params });
  },

  // 爆仓价预估计算 - 马丁格尔
  algoLiquidationPriceMartingale(params: any) {
    return axios.get(
      '@bapi/v1/strategy-trade/algo/liquidation-price/martingale/cal',
      { params }
    );
  },

  // 创建马丁格尔策略
  createFuturesStrategyMartingale(params: any) {
    return axios.post('@bapi/v1/futures-strategy/martingale/create', params, {
      headers: { 'Content-Type': 'application/json' }
    });
  },

  // 创建网格策略
  createFuturesStrategyGrid(params: any) {
    return axios.post('@bapi/v1/futures-strategy/grid/create', params, {
      headers: { 'Content-Type': 'application/json' }
    });
  },
  // 获取进行中策略-网格
  // bapi/v1/futures-strategy/strategy/running-grid
  getRunningGrid(params: any) {
    return axios.get('@bapi/v1/futures-strategy/strategy/running-grid', {
      params
    });
  },
  // 获取进行中策略-马丁格尔
  getRunningMartingale(params: any) {
    return axios.get('@bapi/v1/futures-strategy/strategy/running-martingale', {
      params
    });
  },
  // 获取历史策略-网格
  getHistoryGrid(params: any) {
    return axios.get('@bapi/v1/futures-strategy/strategy/his-grid', { params });
  },
  // 获取历史策略-马丁格尔
  getHistoryMartingale(params: any) {
    return axios.get('@bapi/v1/futures-strategy/strategy/his-martingale', {
      params
    });
  },
  // 修改保证金
  strategyChangeMargin(data: any) {
    return axios.post('@bapi/v1/futures-strategy/modifyMargin', data, {
      headers: { 'Content-Type': 'application/json' }
    });
  },
  // 停止策略-网格
  stopStrategyGrid(params: any) {
    return axios.get('@bapi/v1/futures-strategy/strategy/stop-grid', {
      params
    });
  },
  // 停止策略-马丁格尔
  stopStrategyMartingale(params: any) {
    return axios.get('@bapi/v1/futures-strategy/strategy/stop-martingale', {
      params
    });
  },
  // 资产页面，运行中的机器人
  // /bapi/v1/futures-strategy/strategy-account/running-robot
  getRunningRobot(params: any) {
    return axios.get(
      '@bapi/v1/futures-strategy/strategy-account/running-robot',
      { params }
    );
  },
  // 资产页面，运行中的资产列表
  getRunningAsset(params: any) {
    return axios.get('@bapi/v1/futures-strategy/strategy-account/asset', {
      params
    });
  },
  // martingale-加仓
  addPosition(data: any) {
    return axios.post(
      '@bapi/v1/futures-strategy/martingale/addPosition',
      data,
      {
        headers: { 'Content-Type': 'application/json' }
      }
    );
  },
  // bapi/v1/futures-strategy/grid/update
  // 网格设置 止盈止损
  updateGridStopPrice(data: any) {
    return axios.post('@bapi/v1/futures-strategy/grid/update', data, {
      headers: { 'Content-Type': 'application/json' }
    });
  },
  // 马丁格尔 设置止损
  updateMartingaleStopPrice(data: any) {
    return axios.post('@bapi/v1/futures-strategy/martingale/stopLoss', data, {
      headers: { 'Content-Type': 'application/json' }
    });
  },
  // 马丁格尔 设置止盈
  updateMartingaleProfitPrice(data: any) {
    return axios.post('@bapi/v1/futures-strategy/martingale/stopProfit', data, {
      headers: { 'Content-Type': 'application/json' }
    });
  },
  // 查询可转出保证金
  getStrategyTransferMargin(params: any) {
    return axios.get('@bapi/v1/futures-strategy/transfer-out-available', {
      params
    });
  },
  balanceFlow(params: any) {
    return axios.get('@bapi/v1/futures-strategy/balance_flow', { params });
  }
};

// 盈亏分析
export const assetAnalysisApi = {
  getMainTotalPnl(params: any) {
    return axios.get('@bapi/v1/user_account_analysis/main/total_pnl', {
      params
    });
  },

  getOtherTotalPnl(params: any) {
    return axios.get('@bapi/v1/user_account_analysis/other/total_pnl', {
      params
    });
  },
  mainTodayPnl(params: any) {
    return axios.get('@bapi/v1/user_account_analysis/main/today_pnl', {
      params
    });
  },
  todayPnl(params: any) {
    return axios.get('@bapi/v1/user_account_analysis/other/today_pnl', {
      params
    });
  },
  // 获取用户盈亏日历
  getMainCalendarView(params: object) {
    return axios.get('@bapi/v1/user_account_analysis/main/calendar_view', {
      params
    });
  },

  // 获取用户盈亏日历
  getOtherCalendarView(params: object) {
    return axios.get('@bapi/v1/user_account_analysis/other/calendar_view', {
      params
    });
  },
  // 获取用户交易数量
  getMainTotalTrade(params: object) {
    return axios.get('@bapi/v1/user_account_analysis/main/trade_volume_view', {
      params
    });
  },
  // 获取用户交易数量
  getOtherTotalTrade(params: object) {
    return axios.get('@bapi/v1/user_account_analysis/other/trade_volume_view', {
      params
    });
  },

  getMainAssetComposition(params: object) {
    return axios.get('@bapi/v1/user_account_analysis/main/asset_composition', {
      params
    });
  },
  getOtherAssetComposition(params: object) {
    return axios.get('@bapi/v1/user_account_analysis/other/asset_composition', {
      params
    });
  }
};

// advance
export const academyApi = {
  getAcademyChoiceness(params: any) {
    return axios.get('@halo/v1alpha1/posts', { params });
  }
};
